import { Link } from 'react-router-dom'
import { ArrowRight, Check, Star } from 'lucide-react'
import { PATHS } from '@/routes/paths'
import { formatPrice, cn } from '@/lib/utils'
import type { PremiumPackage } from '@/types/premium'

interface HomeProductCardProps {
  pkg: PremiumPackage
  className?: string
  variant?: 'default' | 'compact'
  /** Fixed-height layout for home split section */
  split?: boolean
  /** Open product detail scrolled to the image gallery */
  focusGallery?: boolean
}

export function HomeProductCard({ pkg, className, variant = 'default', split = false, focusGallery = false }: HomeProductCardProps) {
  const cover = pkg.image ?? pkg.images?.[0]?.url
  const isCompact = variant === 'compact'
  const splitFeatures = pkg.features.filter(Boolean).slice(0, 2)
  const detailHref = focusGallery ? PATHS.premiumProductGallery(pkg.id) : PATHS.premiumProduct(pkg.id)

  return (
    <Link
      to={detailHref}
      className={cn(
        'group flex h-full flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md',
        isCompact && 'rounded-lg',
        split && 'min-h-[328px]',
        className,
      )}
    >
      <div
        className={cn(
          'relative shrink-0 overflow-hidden bg-neutral-50',
          split ? 'h-[118px] p-2' : isCompact ? 'aspect-square p-1.5' : 'aspect-4/3 bg-neutral-100',
        )}
      >
        {cover ? (
          <img
            src={cover}
            alt={pkg.name}
            className={cn(
              'h-full w-full transition-transform duration-500 group-hover:scale-[1.02]',
              isCompact || split ? 'object-contain' : 'object-cover',
            )}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-[10px] text-neutral-400">No image</div>
        )}
        {pkg.popular && (
          <span
            className={cn(
              'absolute left-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-full bg-qbc-navy text-white',
              isCompact ? 'px-1.5 py-0.5 text-[9px]' : 'left-2 top-2 px-2 py-0.5 text-[10px]',
            )}
          >
            <Star className={cn('fill-current', isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
            {isCompact ? 'Top' : 'Popular'}
          </span>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-2.5 sm:p-3">
        <h3
          className={cn(
            'font-display font-bold text-qbc-navy',
            split ? 'line-clamp-2 text-[11px] leading-snug sm:text-xs' : isCompact ? 'line-clamp-2 text-xs leading-snug' : 'line-clamp-2 text-sm',
          )}
        >
          {pkg.name}
        </h3>

        {split ? (
          <>
            {pkg.description && (
              <p className="mt-1 line-clamp-2 text-[10px] leading-snug text-neutral-500">
                {pkg.description}
              </p>
            )}

            {splitFeatures.length > 0 && (
              <ul className="mt-2 flex-1 space-y-1">
                {splitFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-1.5 text-[9px] leading-snug text-neutral-700 sm:text-[10px]">
                    <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-success-50 text-success-600">
                      <Check className="h-2 w-2" strokeWidth={3} />
                    </span>
                    <span className="line-clamp-2">{feature}</span>
                  </li>
                ))}
              </ul>
            )}

            {pkg.duration && (
              <p className="mt-1.5 text-[9px] font-semibold uppercase tracking-wide text-primary-600/75">
                {pkg.duration}
              </p>
            )}
          </>
        ) : (
          <>
            {!isCompact && (
              <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-neutral-500">{pkg.description}</p>
            )}
            {isCompact && pkg.description && (
              <p className="mt-1 line-clamp-1 text-[10px] text-neutral-500">{pkg.description}</p>
            )}
          </>
        )}

        <div
          className={cn(
            'mt-auto flex min-h-[34px] items-center justify-between gap-1 border-t border-neutral-100 pt-2',
            !split && !isCompact && 'gap-2 pt-3',
          )}
        >
          <div className="min-w-0">
            <p
              className={cn(
                'font-display font-extrabold text-primary-600',
                isCompact || split ? 'text-xs' : 'text-base',
              )}
            >
              {formatPrice(pkg.price)}
            </p>
          </div>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-0.5 rounded-full border border-primary-100 bg-primary-50/60 px-2 py-0.5 font-semibold text-primary-700 transition-colors group-hover:border-primary-200 group-hover:bg-primary-50',
              split ? 'text-[9px] sm:text-[10px]' : isCompact ? 'text-[10px]' : 'text-[11px]',
            )}
          >
            View
            <ArrowRight className={split ? 'h-2.5 w-2.5' : isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          </span>
        </div>
      </div>
    </Link>
  )
}
